import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp, HelpCircle, Shield, CreditCard, Cpu, Pencil, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import SEO from '@/components/SEO';
import InternalLinksSection from '@/components/InternalLinksSection';

interface FAQ {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  questions: FAQ[];
}

export const FAQ_DATA: FAQCategory[] = [
  {
    title: "General",
    icon: <HelpCircle className="w-5 h-5" />,
    questions: [
      { question: "What is Ball Simulation?", answer: "A ball simulation is a physics-based animation where digital spheres interact with gravity, boundaries, and each other. It's often used to create 'oddly satisfying' videos and ASMR content." },
      { question: "How do you make satisfying physics videos?", answer: "Using our Creator Studio, you can customize physics parameters like bounciness, friction, and gravity. Syncing these interactions with high-quality sound effects (ASMR) creates the 'satisfying' effect. Check out our [creator tutorials](/tutorials) for step-by-step guides." },
      { question: "Can I use these videos for YouTube Shorts and TikTok?", answer: "Absolutely! Our tool is specifically designed for content creators. You can export vertical videos perfectly formatted for YouTube Shorts, Instagram Reels, and TikTok. Read more on our [YouTube Shorts Generator page](/youtube-shorts-generator) or [ASMR Video Generator page](/asmr-video-generator)." },
      { question: "Is this an AI video generator?", answer: "No, it is not an AI video generator. It is a physics simulation engine that runs in your browser. You have full control over the physics and can export the simulation as a video." },
      { question: "Can I pay with PayPal?", answer: "Yes, we use PayPal and Paddle as our secure payment gateways for both one-time and recurring payments." },
      { question: "Do you offer refunds?", answer: "No, we do not offer refunds on monthly tiers, but we do offer a free plan so you can try before you buy. Compare plan options on our [pricing page](/plans)." },
      { question: "What are the best physics settings for satisfying videos?", answer: "To create 'satisfying' videos, we recommend using high bounciness (0.8+), low friction (0.1-), and a smooth container with plenty of room for the balls to move." },
      { question: "Do I need to be a physicist to use this?", answer: "Absolutely not! The interface is designed for humans, not scientists. You can achieve complex results with simple sliders and presets." },
      { question: "What platforms are supported?", answer: "We support all modern web browsers. The app is also installable as a PWA (Progressive Web App) on iOS, Android, and Desktop." },
      { question: "Can I use it offline?", answer: "Yes, once the app is loaded or installed as a PWA, many core simulation features work offline. However, cloud syncing and asset uploads require an internet connection." },
      { question: "How do I save my creations?", answer: "Creations are saved locally in your browser's IndexedDB. If you have an account, you can sync them to our cloud for cross-device access." },
      { question: "What is 'Fun Circle Games'?", answer: "Fun Circle Games is the studio behind this simulation. We specialize in tactile, satisfying, and visually appealing digital experiences." },
      { question: "How long has this been in development?", answer: "The project started in early 2024 and has evolved through several iterations to the complex engine it is today." },
      { question: "Can I share my simulations with friends?", answer: "Yes! You can export your simulation settings as a JSON file or use our built-in sharing link feature." },
      { question: "Is there a mobile app?", answer: "We focus on a high-fidelity web experience which you can 'Add to Home Screen' to get a native app feel without the app store bloat." },
      { question: "Who is the target audience?", answer: "Digital artists, content creators (YouTube/TikTok), physics enthusiasts, and anyone looking for a meditative, 'satisfying' experience." },
      { question: "What makes this simulation 'Hypnotic'?", answer: "The combination of perfect rhythmic timing, color harmonies, and predictable yet complex physics patterns creates a 'flow state' for the viewer." },
      { question: "How do I report a bug?", answer: "You can use the 'Report a Bug' button in the dashboard, contact us directly via the [contact page](/contact), or submit a ticket in our [Community Discord](https://discord.gg/cdwNFdbx)." },
      { question: "Are there community challenges?", answer: "Yes, we regularly post simulation prompts on our YouTube and Instagram channels." },
      { question: "Does the simulation run on my GPU?", answer: "Yes, we use WebGL for rendering and compute-heavy calculations to ensure smooth performance even with hundreds of balls." },
      { question: "Can I use this for university research?", answer: "While intended for art and entertainment, the physics engine is mathematically grounded and can be used for basic educational purposes." },
      { question: "Why is the logo a circle?", answer: "The circle represents the infinite nature of physics and the core geometry of our simulations." },
      { question: "Can I run multiple simulations at once?", answer: "Our Rhythmic Mode supports 'Multi-Sim' which allows you to view several independent simulations in a grid." },
      { question: "What is the 'Zen' mode?", answer: "Zen mode removes all UI, HUDs, and buttons, giving you a pure, uninterrupted view of your simulation." },
      { question: "Do you have a newsletter?", answer: "Yes, you can sign up at the bottom of the home page for monthly updates on new features and presets." }
    ]
  },
  {
    title: "Plans & Business Model",
    icon: <CreditCard className="w-5 h-5" />,
    questions: [
      { question: "How does the business model work?", answer: "We operate on a freemium model. Core simulation tools are free, while professional features and high-capacity storage require a subscription. View detailed pricing on our [plans page](/plans)." },
      { question: "What is included in the FREE plan?", answer: "The FREE plan includes 10 local saves, basic physics controls, 5 concurrent balls, and access to standard visual effects. See more details on our [plans page](/plans)." },
      { question: "What are the benefits of the PRO plan?", answer: "PRO unlocks unlimited local saves, 50 balls, custom sound uploads, and advanced MIDI filtering tools. Compare pricing tiers on our [plans page](/plans)." },
      { question: "What is the ELITE plan?", answer: "ELITE is for heavy users. It includes 100 concurrent balls, cloud sync for all saves, priority rendering, and early access to new modes. See the full breakdown on our [plans page](/plans)." },
      { question: "Do you offer an ENTERPRISE plan?", answer: "Yes, for studios and commercial users needing white-label exports and custom feature development. Check our [pricing page](/plans) or [contact support](/contact) for a custom quote." },
      { question: "Can I pay with PayPal?", answer: "Yes, we use PayPal as our primary secure payment gateway for both one-time and recurring payments." },
      { question: "Is there a free trial for PRO?", answer: "We occasionally offer 7-day trials. Check your dashboard for current promotions." },
      { question: "Can I cancel my subscription anytime?", answer: "Yes, you can cancel your subscription from your billing settings. You will retain access until the end of your current billing period." },
      { question: "Are there any hidden fees?", answer: "No. The price you see is the price you pay. Taxes are calculated based on your region at checkout." },
      { question: "Does my plan affect simulation speed?", answer: "No, simulation speed is limited by your hardware. However, higher plans allow for more simultaneous objects (balls)." },
      { question: "Can I use simulations for commercial YouTube videos?", answer: "ELITE and ENTERPRISE plans grant full commercial usage rights. FREE and PRO plans are for personal/social media use (with credit)." },
      { question: "Do you offer student discounts?", answer: "Yes, we offer 50% off PRO for verified students. Contact us with your ID." },
      { question: "What happens if I stop paying?", answer: "Your account reverts to FREE. You won't lose your high-tier saves, but you may be unable to create new ones until you are below the limit." },
      { question: "Is my payment info secure?", answer: "We never store your credit card details. All transactions are handled by PayPal's industry-standard secure servers." },
      { question: "Do you offer refunds?", answer: "We have a 7-day refund policy for annual plans if you are unhappy with the service. See our Refund Policy page for details." },
      { question: "Why do some settings have locks?", answer: "Settings with locks are premium features reserved for specific plan levels (PRO, ELITE, or ENTERPRISE)." },
      { question: "Can I upgrade from PRO to ELITE mid-month?", answer: "Yes, you only pay the prorated difference for the remainder of the month." },
      { question: "Are there yearly plans?", answer: "Yes, yearly plans offer a 20% discount compared to paying monthly." },
      { question: "Do you have a referral program?", answer: "Not currently, but it is in our roadmap for later this year!" },
      { question: "Is there a limit on cloud storage?", answer: "FREE is local only. PRO has 100MB cloud space. ELITE has 1GB cloud space. ENTERPRISE has custom limits." }
    ]
  },
  {
    title: "Technical & Performance",
    icon: <Cpu className="w-5 h-5" />,
    questions: [
      { question: "How do I improve performance?", answer: "Lower the ball count, disable complex parallax effects, or use a smaller window size. You can also toggle 'Optimization Mode' in settings." },
      { question: "What is a 'Physics Worker'?", answer: "It's a separate thread that handles all the math. This keeps the UI responsive even when the simulation gets intense." },
      { question: "Can I export my simulation as a video?", answer: "ELITE users can use our cloud-based video export tool to render high-resolution MP4s. Read our [Physics Video Generator](/physics-video-generator) and [Faceless Video Generator](/faceless-video-generator) pages for details." },
      { question: "Does this use AI?", answer: "We use AI for our 'Smart Input' feature which suggests parameters based on your text descriptions, and for some song-to-midi conversions." },
      { question: "Which browser is best for this?", answer: "Google Chrome and Microsoft Edge typically offer the best WebGL performance, but Firefox and Safari are fully supported." },
      { question: "Can I use MIDI controllers?", answer: "Yes! High-tier plans support WebMIDI, allowing you to trigger simulation events with your physical keyboard or pad." },
      { question: "How accurate is the physics?", answer: "We use a custom Verloc/Euler integration engine that prioritizes visual stability and 'satisfaction' over rigorous scientific accuracy." },
      { question: "What is 'Spectral Analysis'?", answer: "Our engine analyzes audio frequencies in real-time to make visual elements react to different instruments in your music." },
      { question: "Can I change the frame rate?", answer: "The engine targets 60fps but can go up to 144fps on high-refresh-rate monitors if the 'V-Sync' setting is enabled." },
      { question: "How are sounds generated?", answer: "We use Tone.js for high-quality FM and Subtractive synthesis, allowing for thousands of simultaneous voices." },
      { question: "What is 'Sub-stepping'?", answer: "It's a technique where the physics engine calculates multiple steps per frame to prevent balls from passing through walls at high speeds." },
      { question: "Can I import custom shaders?", answer: "Currently, we only allow built-in shaders for security reasons, but ENTERPRISE users can request custom shader development." },
      { question: "How does 'Spatial Gridding' work?", answer: "It's an optimization that only checks for collisions between balls that are near each other, drastically reducing CPU load." },
      { question: "Is there a limit on resolution?", answer: "The simulation renders at your display's native resolution. Cloud exports can go up to 4K." },
      { question: "Does the simulation work on mobile GPUs?", answer: "Yes, but we recommend keeping ball counts below 15 for a smooth experience on older phones." },
      { question: "What file format are presets saved in?", answer: "Presets are saved as structured JSON objects that include every setting from physics to post-processing." },
      { question: "Can I use this on a VR headset?", answer: "Some of our parallax backgrounds support VR/360 projection when viewed through a browser in a headset." },
      { question: "How often do you push technical updates?", answer: "We update the physics engine and renderers bi-weekly." },
      { question: "Do you use cookies?", answer: "We only use essential cookies for authentication and performance metrics. See our Privacy Policy." },
      { question: "Can I run the simulation on my own server?", answer: "No, the software is provided as a Service (SaaS). We do not currently offer a self-hosted version." }
    ]
  },
  {
    title: "Creator Studio",
    icon: <Pencil className="w-5 h-5" />,
    questions: [
      { question: "What is 'Creator Studio'?", answer: "It's the heart of the app where you have total control over the simulation parameters, visual styles, and audio workshop." },
      { question: "How do I upload custom sounds?", answer: "Go to Audio Workshop > Custom Uploads. You can drag and drop WAV or MP3 files there." },
      { question: "What are 'Impact FX'?", answer: "These are visual particles and glows that trigger whenever a ball hits a boundary or another ball." },
      { question: "Can I layer different backgrounds?", answer: "Yes, you can use the 'Layer Sorting' section to stack parallax effects and solid backgrounds." },
      { question: "How do I create a 'Rhythmic' simulation?", answer: "Select RHYTHMIC mode, upload a MIDI file, and the balls will spawn or hit walls in sync with the musical notes." },
      { question: "What are 'Ball Spawners'?", answer: "Spawners are points in the simulation that automatically generate new balls at specific intervals or on musical cues." },
      { question: "Can I change the 'Gravity' orientation?", answer: "Yes! You can rotate the gravity vector 360 degrees or even disable it for zero-G simulations." },
      { question: "What is the 'Visual Slicer'?", answer: "The Visual Slicer allows you to apply different rendering styles to specific regions of the simulation canvas." },
      { question: "Can I animate the simulation parameters?", answer: "Yes, using our 'Automation' tools, you can set keyframes for things like speed, color, and size over time." },
      { question: "What is 'Song Converter'?", answer: "It's an AI-powered tool that attempts to transcribe a standard MP3 into a MIDI file suitable for Rhythmic mode." },
      { question: "How do I use 'Midi Filtering'?", answer: "You can filter notes by pitch, velocity, or channel to create complex musical visualizations." },
      { question: "Can I change the ball shape?", answer: "Yes, you can choose from circles, squares, triangles, and even custom SVG paths (ELITE only)." },
      { question: "What is 'Space Spiral' mode?", answer: "A specialized mode where balls are trapped in a vortex, creating infinite spiral patterns." },
      { question: "Can I add a HUD to my video?", answer: "Yes, we have several HUD presets that show real-time stats like BPM, Ball Count, and Peak Frequencies." },
      { question: "How do I reset my workshop?", answer: "Use the 'Reset to Default' button in the General section to clear all current modifications." },
      { question: "Can I save 'Presets' within the studio?", answer: "Yes, you can name and save your current configuration to a 'Preset Library' for quick access later." },
      { question: "How do I control the camera?", answer: "You can use auto-follow modes, set camera paths, or use manual drag controls to find the perfect angle." },
      { question: "What is 'Post-Processing'?", answer: "It involves filters like Bloom, Chromatic Aberration, and Grain that apply to the final rendered image." },
      { question: "Can I change the ball's 'Bounciness'?", answer: "Yes, the 'Restitution' slider in Physics settings controls how much energy is kept after a bounce." },
      { question: "How do I make a 'Rainbow' trail?", answer: "Enable Trails and select 'Dynamic Hue' in the Trail settings." }
    ]
  },
  {
    title: "Support & Legal",
    icon: <Shield className="w-5 h-5" />,
    questions: [
      { question: "Is my data private?", answer: "Yes. Your creations belong to you. We do not use your private simulations for training models without consent." },
      { question: "Can I delete my account?", answer: "Yes, you can permanently delete your account and all associated data from the profile settings page." },
      { question: "What happens to my data if you shut down?", answer: "We have a commitment to release an open-source viewer if the service is ever discontinued." },
      { question: "How do I contact support?", answer: "Email support@funcirclegames.com, open a support ticket in our [Community Discord](https://discord.gg/cdwNFdbx), or use the contact form on our [contact page](/contact)." },
      { question: "Are my simulations copyrighted?", answer: "The visual output is yours. However, the software and engine code remain the intellectual property of Fun Circle Games." },
      { question: "Do you offer enterprise-level SLAs?", answer: "Yes, for our ENTERPRISE customers. Please inquire via the [contact page](/contact)." },
      { question: "Is the app GDPR compliant?", answer: "Yes, we are fully compliant with GDPR and CCPA regulations." },
      { question: "Can I use your music in my videos?", answer: "Our library music is royalty-free for subscribers. Free users must provide credit." },
      { question: "Where are your servers located?", answer: "We use a global edge network with primary data centers in the US and EU for low latency." },
      { question: "Do you track my location?", answer: "No, we only use IP addresses for basic security and load balancing." },
      { question: "How do I change my password?", answer: "You can change or reset your password from the security tab in your profile." },
      { question: "What is your refund policy for duplicate charges?", answer: "Contact us within 30 days and we will issue an immediate refund for any system errors or duplicates." },
      { question: "Can I transfer my ELITE plan to another user?", answer: "No, subscriptions are tied to individual accounts for security reasons." },
      { question: "Is children's usage allowed?", answer: "The app is rated for ages 13+. Children under 13 should use the platform with parental supervision." },
      { question: "Do you have a vulnerability disclosure program?", answer: "Yes, we appreciate security researchers. Please contact security@funcirclegames.com." },
      { question: "Can I download the source code?", answer: "No, the source code is proprietary." },
      { question: "What libraries do you use?", answer: "Key libraries include React, Tailwind CSS, Tone.js, and Pixi.js/WebGL." },
      { question: "Are there limits on API usage?", answer: "Yes, rate limits apply to all accounts to prevent abuse. PREMIUM accounts have higher thresholds." },
      { question: "Do you support dark mode?", answer: "Yes! You can toggle between Light and Dark themes in the dashboard." },
      { question: "Can I be an affiliate?", answer: "We are developing an affiliate portal. Register your interest via the contact form!" }
    ]
  }
];

const FAQPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const filteredData = useMemo(() => {
    if (!searchQuery) return FAQ_DATA;
    return FAQ_DATA.map(category => ({
      ...category,
      questions: category.questions.filter(q =>
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(category => category.questions.length > 0);
  }, [searchQuery]);

  const toggleFAQ = (catIndex: number, qIndex: number) => {
    const key = `${catIndex}-${qIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  const totalQuestions = FAQ_DATA.reduce((acc, cat) => acc + cat.questions.length, 0);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="FAQ - Hypnotic Ball Simulation"
        description="Find answers to all your questions about our physics engine, business model, and creator tools."
      />

      {/* Hero Section - Normal Height */}
      <section className="relative pt-32 pb-16 px-6 border-b border-cyan-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-50/30 to-white -z-10" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-100/30 blur-[100px] -z-10" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-50/20 blur-[100px] -z-10" />

        <div className="max-w-3xl mx-auto text-center space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-600 text-white text-[10px] font-black uppercase tracking-[0.2em]"
          >
            Help Center
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl font-black font-display tracking-tight text-cyan-950 uppercase"
          >
            Got Questions? We Have <span className="text-cyan-600">Answers</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-zinc-500 text-base font-medium"
          >
            Explore {totalQuestions}+ commonly asked questions about our business model,
            technical engine, and creative tools.
          </motion.p>
        </div>
      </section>

      {/* Search Bar Section */}
      <section className="sticky top-16 z-20 bg-white/80 backdrop-blur-md border-b border-cyan-50 py-4 px-6 shadow-sm">
        <div className="max-w-3xl mx-auto relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
          <input
            type="text"
            placeholder="Search for questions (e.g. 'plans', 'physics', 'MIDI')..."
            className="w-full bg-white border border-cyan-100 rounded-2xl py-3.5 pl-12 pr-6 text-sm font-medium focus:outline-none focus:ring-4 focus:ring-cyan-600/5 focus:border-cyan-600 transition-all shadow-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </section>

      {/* FAQ Content */}
      <section className="max-w-4xl mx-auto py-16 px-6">
        <div className="space-y-16">
          {filteredData.length > 0 ? (
            filteredData.map((category, catIndex) => (
              <div key={catIndex} className="space-y-6">
                <div className="flex items-center gap-3 border-b border-cyan-50 pb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600">
                    {category.icon}
                  </div>
                  <h2 className="text-xl font-black font-display uppercase tracking-wider text-cyan-950">
                    {category.title}
                  </h2>
                </div>

                <div className="grid gap-3">
                  {category.questions.map((faq, qIndex) => {
                    const isOpen = openIndex === `${catIndex}-${qIndex}`;
                    return (
                      <motion.div
                        key={qIndex}
                        layout
                        className={`group border rounded-2xl transition-all duration-300 ${isOpen
                          ? 'border-cyan-600 bg-cyan-50/30 shadow-md'
                          : 'border-cyan-50 bg-white hover:border-cyan-200 hover:shadow-sm'
                          }`}
                      >
                        <button
                          onClick={() => toggleFAQ(catIndex, qIndex)}
                          className="w-full text-left p-5 flex items-center justify-between gap-4"
                        >
                          <span className={`font-bold transition-colors ${isOpen ? 'text-cyan-950' : 'text-zinc-700'}`}>
                            {faq.question}
                          </span>
                          <div className={`shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                            <ChevronDown className={`w-4 h-4 ${isOpen ? 'text-cyan-600' : 'text-zinc-400'}`} />
                          </div>
                        </button>

                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="px-5 pb-5 pt-0">
                                <p className="text-zinc-600 leading-relaxed font-medium">
                                  {renderTextWithLinks(faq.answer)}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-20 bg-zinc-50 rounded-3xl border-2 border-dashed border-zinc-100">
              <Search className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-zinc-950">No matching questions found</h3>
              <p className="text-zinc-500 max-w-xs mx-auto mt-2">
                Try searching with different keywords or browse our categories below.
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-6 px-4 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-black uppercase text-zinc-600 hover:border-zinc-400 transition-colors"
              >
                Clear Search
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="bg-cyan-50/30 py-20 px-6 border-t border-cyan-50 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-cyan-100 mx-auto transform rotate-6">
            <MessageSquare className="w-8 h-8 text-cyan-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-3xl font-black font-display text-cyan-950">Still Have Questions?</h2>
            <p className="text-zinc-500 font-medium">
              We're here to help. Reach out to our support team and we'll get back to you within 24 hours.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              className="px-8 py-3 bg-cyan-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-cyan-700 transition-all hover:scale-105 active:scale-95 shadow-xl border border-white/10"
            >
              Contact Support
            </Link>
            <a
              href="https://discord.gg/cdwNFdbx"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-[#5865F2] text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#4752C4] transition-all hover:scale-105 active:scale-95 shadow-xl border border-white/10"
            >
              Join Discord
            </a>
            <a
              href="mailto:support@funcirclegames.com"
              className="px-8 py-3 bg-white text-cyan-950 border border-cyan-200 rounded-xl text-xs font-black uppercase tracking-widest hover:border-cyan-950 transition-all active:scale-95 shadow-sm"
            >
              Send Email
            </a>
          </div>
        </div>
      </section>

      <InternalLinksSection />
    </div>
  );
};

const renderTextWithLinks = (text: string) => {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const linkText = match[1];
    const linkUrl = match[2];

    if (linkUrl.startsWith('mailto:') || linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
      parts.push(
        <a 
          key={match.index} 
          href={linkUrl} 
          className="text-cyan-600 hover:text-cyan-800 underline decoration-cyan-600/30 hover:decoration-cyan-800 transition-colors cursor-pointer font-bold"
          target={linkUrl.startsWith('mailto:') ? undefined : '_blank'}
          rel={linkUrl.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
        >
          {linkText}
        </a>
      );
    } else {
      parts.push(
        <Link 
          key={match.index} 
          to={linkUrl} 
          className="text-cyan-600 hover:text-cyan-800 underline decoration-cyan-600/30 hover:decoration-cyan-800 transition-colors cursor-pointer font-bold"
        >
          {linkText}
        </Link>
      );
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

export default FAQPage;
